// BCG 스코어링 및 분석 함수들

import { WindowData, MindScoreResult, PSDData, StressAnalysis, BalanceAnalysis, CalibrationPair } from './types';

/**
 * Robust Z-score 계산
 */
export function robustZ(values: number[]): number[] {
  const validValues = values.filter(v => isFinite(v));
  if (validValues.length === 0) return values.map(() => NaN);

  const median = calculateMedian(validValues);
  const deviations = validValues.map(v => Math.abs(v - median));
  const mad = calculateMedian(deviations) + 1e-9;
  
  return values.map(v => isFinite(v) ? (v - median) / (1.4826 * mad) : NaN);
}

/**
 * 중앙값 계산
 */
function calculateMedian(arr: number[]): number {
  const sorted = [...arr].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 
    ? (sorted[mid - 1] + sorted[mid]) / 2 
    : sorted[mid];
}

/**
 * 상대적 마음건강 점수 계산
 */
export function mindScoreRelative(windows: WindowData[]): number {
  const validWindows = windows.filter(w => w.DQI >= 0.6 && w.rr_count >= 3);
  if (validWindows.length === 0) return NaN;

  const rmssdValues = validWindows.map(w => w.RMSSD_ms).filter(v => isFinite(v));
  const hrValues = validWindows.map(w => w.HR_bpm).filter(v => isFinite(v));
  const respValues = validWindows.map(w => w.Resp_bpm).filter(v => isFinite(v));

  const zRMSSD = robustZ(rmssdValues);
  const zHR = robustZ(hrValues);
  const zResp = robustZ(respValues);

  const validIndices = zRMSSD.map((_, i) => 
    isFinite(zRMSSD[i]) && isFinite(zHR[i]) && isFinite(zResp[i]) ? i : -1
  ).filter(i => i >= 0);

  if (validIndices.length === 0) return NaN;

  const hRaw = validIndices.map(i => 
    zRMSSD[i] - zHR[i] - 0.5 * zResp[i]
  );

  const hRawMedian = calculateMedian(hRaw);
  return 50.0 + 35.0 * Math.tanh(hRawMedian / 1.5);
}

/**
 * 절대적 심박수 점수
 */
function scoreHRAbsolute(hr: number): number {
  if (!isFinite(hr)) return NaN;
  
  if (hr <= 55) return 100.0;
  if (hr <= 65) return 100.0 - (hr - 55) * 1.0;
  if (hr <= 80) return 90.0 - (hr - 65) * 1.333;
  if (hr <= 100) return 70.0 - (hr - 80) * 2.0;
  if (hr <= 120) return 30.0 - (hr - 100) * 0.5;
  return 15.0;
}

/**
 * 절대적 RMSSD 점수
 */
function scoreRMSSDAbsolute(rmssd: number): number {
  if (!isFinite(rmssd)) return NaN;
  
  const x = Math.max(5, Math.min(200, rmssd));
  if (x <= 15) return 10.0;
  if (x <= 35) return 10 + (x - 15) * 2.5;
  if (x <= 55) return 60 + (x - 35) * 1.0;
  if (x <= 70) return 80 + (x - 55) * 0.666;
  if (x <= 120) return 90 + (x - 70) * 0.2;
  return 100.0;
}

/**
 * 절대적 호흡 점수
 */
function scoreRespAbsolute(resp: number): number {
  if (!isFinite(resp)) return NaN;
  if (resp < 6 || resp > 30) return 30.0;
  return 60 + 40 * Math.exp(-((resp - 14.0) ** 2) / (2 * 4.0 ** 2));
}

/**
 * 절대적 마음건강 점수 계산 (최적 5분 선택)
 */
export function mindScoreAbsolute(windows: WindowData[]): MindScoreResult {
  const validWindows = windows.filter(w => w.DQI >= 0.7 && w.rr_count >= 3);
  if (validWindows.length === 0) {
    return { score: NaN };
  }

  const sortedWindows = [...validWindows].sort((a, b) => a.t_start.getTime() - b.t_start.getTime());
  const width = Math.min(20, sortedWindows.length);
  
  let bestIndex = 0;
  let bestValue = -1e9;

  // 최적 5분(20개 윈도우) 블록 찾기
  for (let i = 0; i <= sortedWindows.length - width; i++) {
    const block = sortedWindows.slice(i, i + width);
    
    // 연속성 확인
    const maxGap = Math.max(...block.slice(1).map((w, idx) => 
      (w.t_start.getTime() - block[idx].t_end.getTime()) / 1000
    ));
    const isContinuous = maxGap <= 20.0;
    
    if (isContinuous) {
      const rmssdMed = calculateMedian(block.map(w => w.RMSSD_ms).filter(v => isFinite(v)));
      const hrMed = calculateMedian(block.map(w => w.HR_bpm).filter(v => isFinite(v)));
      const dqiMed = calculateMedian(block.map(w => w.DQI).filter(v => isFinite(v)));
      
      const value = rmssdMed - hrMed + 10 * dqiMed;
      if (value > bestValue) {
        bestValue = value;
        bestIndex = i;
      }
    }
  }

  const selectedWindows = sortedWindows.slice(bestIndex, bestIndex + width);
  
  const rmssdMed = calculateMedian(selectedWindows.map(w => w.RMSSD_ms).filter(v => isFinite(v)));
  const hrMed = calculateMedian(selectedWindows.map(w => w.HR_bpm).filter(v => isFinite(v)));
  const respMed = calculateMedian(selectedWindows.map(w => w.Resp_bpm).filter(v => isFinite(v)));
  
  const sRMSSD = scoreRMSSDAbsolute(rmssdMed);
  const sHR = scoreHRAbsolute(hrMed);
  const sResp = scoreRespAbsolute(respMed);
  
  const score = 0.6 * sRMSSD + 0.3 * sHR + 0.1 * sResp;

  return {
    score,
    selectedWindows,
    components: {
      RMSSD_med: rmssdMed,
      HR_med: hrMed,
      Resp_med: respMed,
      s_RMSSD: sRMSSD,
      s_HR: sHR,
      s_Resp: sResp
    }
  };
}

/**
 * 의사결정 라벨
 */
export function getDecisionLabel(score: number): string {
  if (!isFinite(score)) return 'insufficient_data';
  if (score >= 80) return 'healthy';
  if (score >= 70) return 'normal';
  if (score >= 55) return 'caution';
  return 'risk';
}

/**
 * 스트레스 점수 계산
 */
export function calculateStressScore(windows: WindowData[], psd: PSDData): StressAnalysis {
  const validWindows = windows.filter(w => w.DQI >= 0.6 && w.rr_count >= 3);
  if (validWindows.length === 0) {
    return {
      score: NaN,
      category: 'insufficient_data',
      description: 'mentalHealth.stress.description.insufficient'
    };
  }

  const hrValues = validWindows.map(w => w.HR_bpm).filter(v => isFinite(v));
  const rmssdValues = validWindows.map(w => w.RMSSD_ms).filter(v => isFinite(v));
  const respValues = validWindows.map(w => w.Resp_bpm).filter(v => isFinite(v));

  const zHR = robustZ(hrValues);
  const zRMSSD = robustZ(rmssdValues);
  const zResp = robustZ(respValues);

  const lfhf = psd.LF_HF;
  const termPSD = isFinite(lfhf) ? Math.log(lfhf + 1e-6) : 0.0;

  const hrMed = calculateMedian(zHR.filter(v => isFinite(v)));
  const rmssdMed = calculateMedian(zRMSSD.filter(v => isFinite(v)));
  const respMed = calculateMedian(zResp.filter(v => isFinite(v)));

  const raw = hrMed - rmssdMed + 0.5 * respMed + 0.5 * termPSD;
  const score = 50 + 35 * Math.tanh(raw / 1.5);

  return {
    score,
    category: getStressCategory(score),
    description: getStressDescription(score)
  };
}

/**
 * 스트레스 카테고리
 */
function getStressCategory(score: number): string {
  if (!isFinite(score)) return 'insufficient_data';
  if (score < 40) return 'low';
  if (score < 60) return 'normal';
  if (score < 80) return 'high';
  return 'very_high';
}

/**
 * 스트레스 설명
 */
function getStressDescription(score: number): string {
  const category = getStressCategory(score);
  
  switch (category) {
    case 'low':
      return 'mentalHealth.stress.description.low';
    case 'normal':
      return 'mentalHealth.stress.description.normal';
    case 'high':
    case 'very_high':
      return 'mentalHealth.stress.description.high';
    default:
      return 'mentalHealth.stress.description.insufficient';
  }
}

/**
 * 자율신경 균형 분석
 */
export function analyzeBalance(lfnu: number, hfnu: number): BalanceAnalysis {
  if (!isFinite(lfnu) || !isFinite(hfnu)) {
    return {
      flag: 'insufficient_data',
      description: 'mentalHealth.balance.description.insufficient'
    };
  }

  if (lfnu >= 35 && lfnu <= 65 && hfnu >= 35 && hfnu <= 65) {
    return {
      flag: 'normal',
      description: 'mentalHealth.balance.description.normal'
    };
  }

  if (hfnu > 65) {
    return {
      flag: 'caution',
      description: 'mentalHealth.balance.description.parasympatheticDominant'
    };
  }

  if (lfnu > 65) {
    return {
      flag: 'caution',
      description: 'mentalHealth.balance.description.sympatheticDominant'
    };
  }

  return {
    flag: 'caution',
    description: 'mentalHealth.balance.description.imbalanced'
  };
}

/**
 * 호흡 상태 분석
 */
export function analyzeRespirationState(bpm: number): string {
  if (!isFinite(bpm)) return 'insufficient_data';
  if (bpm < 10) return 'slow';
  if (bpm <= 20) return 'normal';
  return 'fast';
}

/**
 * 캘리브레이션 함수 생성
 */
export function buildCalibrator(pairs: CalibrationPair[]): (x: number) => number {
  if (!pairs || pairs.length === 0) {
    return (x: number) => x;
  }

  const sortedPairs = [...pairs].sort((a, b) => a.input - b.input);
  
  // 중복 제거
  const uniquePairs: CalibrationPair[] = [];
  for (const pair of sortedPairs) {
    if (uniquePairs.length === 0 || pair.input > uniquePairs[uniquePairs.length - 1].input) {
      uniquePairs.push(pair);
    }
  }

  if (uniquePairs.length === 1) {
    const offset = uniquePairs[0].output - uniquePairs[0].input;
    return (x: number) => x + offset;
  }

  return (x: number) => {
    if (x <= uniquePairs[0].input) {
      const slope = (uniquePairs[1].output - uniquePairs[0].output) / 
                   (uniquePairs[1].input - uniquePairs[0].input);
      return uniquePairs[0].output + slope * (x - uniquePairs[0].input);
    }

    if (x >= uniquePairs[uniquePairs.length - 1].input) {
      const lastIdx = uniquePairs.length - 1;
      const slope = (uniquePairs[lastIdx].output - uniquePairs[lastIdx - 1].output) / 
                   (uniquePairs[lastIdx].input - uniquePairs[lastIdx - 1].input);
      return uniquePairs[lastIdx].output + slope * (x - uniquePairs[lastIdx].input);
    }

    // 선형 보간
    for (let i = 1; i < uniquePairs.length; i++) {
      if (x <= uniquePairs[i].input) {
        const slope = (uniquePairs[i].output - uniquePairs[i - 1].output) / 
                     (uniquePairs[i].input - uniquePairs[i - 1].input);
        return uniquePairs[i - 1].output + slope * (x - uniquePairs[i - 1].input);
      }
    }

    return x;
  };
}

